import React, { useState, useRef, useEffect, useCallback } from "react";
import { FaRegCopy } from "react-icons/fa";
import {
  FiEdit3,
  FiCamera,
  FiDownload,
  FiMinus,
  FiMaximize2,
  FiX,
} from "react-icons/fi";

import icons from "../img/icons.svg";

import styles from "../css/EditCharsModal.module.css";

// ID symbolů dostupných ve SVG spritu /img/icons.svg.
const ICON_IDS = [
  "affilate", "basket", "campaign", "cite", "click", "clock", "close",
  "color-clock", "color-online", "converse", "credit", "danger", "dashboard",
  "delete", "document", "download", "email", "eye", "feed", "grow", "hand",
  "heart", "help", "home", "home-2", "chart", "chat", "indicator", "login",
  "meter", "money", "minus", "open", "phone", "plus", "products", "publish",
  "settings", "shield", "statistics", "success", "thumb", "turnon", "user",
  "wallet", "zoom",
];

// Plovoucí, přesouvatelný panel pro vkládání speciálních znaků
// do právě editovaného prvku (contentEditable). Zobrazuje se jen
// během editačního režimu (Alt+klik), který řídí App.js.

// Skupiny znaků. label = popisek tlačítka, char = vkládaný znak.
// Pro nezlomitelnou mezeru používáme title pro lepší orientaci.
const GROUPS = [
  {
    title: "Mezery",
    items: [
      { label: "nbsp", char: " ", title: "Nezlomitelná mezera (U+00A0)" },
    ],
  },
  {
    title: "Uvozovky a pomlčky",
    items: [
      { label: "„", char: "„", title: "Česká dolní uvozovka" },
      { label: "“", char: "“", title: "Česká horní uvozovka" },
      { label: "‚", char: "‚", title: "Jednoduchá dolní" },
      { label: "‘", char: "‘", title: "Jednoduchá horní" },
      { label: "»", char: "»", title: "Pravá francouzská" },
      { label: "«", char: "«", title: "Levá francouzská" },
      { label: "– en", char: "–", title: "En dash" },
      { label: "— em", char: "—", title: "Em dash" },
      { label: "…", char: "…", title: "Výpustka" },
    ],
  },
  {
    title: "Symboly",
    items: [
      { label: "©", char: "©", title: "Copyright" },
      { label: "®", char: "®", title: "Registered" },
      { label: "™", char: "™", title: "Trademark" },
      { label: "€", char: "€", title: "Euro" },
      { label: "Kč", char: "Kč", title: "Koruna" },
      { label: "°", char: "°", title: "Stupeň" },
      { label: "×", char: "×", title: "Krát" },
      { label: "÷", char: "÷", title: "Děleno" },
      { label: "±", char: "±", title: "Plus minus" },
      { label: "‰", char: "‰", title: "Promile" },
      { label: "•", char: "•", title: "Odrážka" },
      { label: "→", char: "→", title: "Šipka vpravo" },
      { label: "←", char: "←", title: "Šipka vlevo" },
      { label: "↑", char: "↑", title: "Šipka nahoru" },
      { label: "↓", char: "↓", title: "Šipka dolů" },
      { label: "✓", char: "✓", title: "Fajfka" },
      { label: "✗", char: "✗", title: "Křížek" },
      { label: "★", char: "★", title: "Hvězda" },
      { label: "½", char: "½", title: "Jedna polovina" },
      { label: "¼", char: "¼", title: "Jedna čtvrtina" },
      { label: "¾", char: "¾", title: "Tři čtvrtiny" },
    ],
  },
  {
    title: "Emotikony",
    items: [
      "😀", "😉", "😍", "🤩", "😎", "🙂", "😇", "🤔", "👍", "👎",
      "👏", "🙌", "🙏", "💪", "👌", "✌️", "🤝", "❤️", "🔥", "⭐",
      "✨", "🎉", "🚀", "💡", "✅", "❌", "⚠️", "📈", "📉", "💰",
      "🛒", "📦", "🎯", "🏆", "🔔", "📣", "💬", "📍", "⏰", "🆕",
    ].map((e) => ({ label: e, char: e, title: e })),
  },
];

const EditCharsModal = ({
  onInsert,
  onInsertIcon,
  onCopyText,
  onSnapshotClipboard,
  onSnapshotFile,
  onClose,
}) => {
  // Výchozí pozice vpravo nahoře, ale ne přímo u kraje.
  const [pos, setPos] = useState({ x: window.innerWidth - 360, y: 80 });
  const [collapsed, setCollapsed] = useState(false);
  // Krátká zpětná vazba po akci (např. "Zkopírováno ✓").
  const [feedback, setFeedback] = useState("");
  const feedbackTimer = useRef(null);
  const dragRef = useRef(null);

  // Drag stav držíme v refu, ať se kvůli mousemove nererenderuje pořád.
  const drag = useRef({ active: false, offsetX: 0, offsetY: 0 });

  const onMouseMove = useCallback((e) => {
    if (!drag.current.active) return;
    const x = e.clientX - drag.current.offsetX;
    const y = e.clientY - drag.current.offsetY;
    // Udržíme panel uvnitř okna.
    const maxX = window.innerWidth - 60;
    const maxY = window.innerHeight - 40;
    setPos({
      x: Math.min(Math.max(0, x), maxX),
      y: Math.min(Math.max(0, y), maxY),
    });
  }, []);

  const onMouseUp = useCallback(() => {
    drag.current.active = false;
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const startDrag = (e) => {
    // Jen levým tlačítkem a jen za hlavičku.
    if (e.button !== 0) return;
    const rect = dragRef.current.getBoundingClientRect();
    drag.current = {
      active: true,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
    };
    document.body.style.userSelect = "none";
  };

  // mousedown s preventDefault → tlačítko neukradne focus editovanému prvku,
  // takže se nespustí blur a kurzor zůstane na svém místě.
  const handleInsert = (e, char) => {
    e.preventDefault();
    onInsert(char);
  };

  // Vloží SVG ikonu ze spritu; stejně jako u znaků nekrademe focus.
  const handleInsertIcon = (e, iconId) => {
    e.preventDefault();
    onInsertIcon(iconId);
  };

  // Vyčistíme případný běžící timer feedbacku při odmountování.
  useEffect(() => {
    return () => {
      if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    };
  }, []);

  const showFeedback = (msg) => {
    setFeedback(msg);
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => setFeedback(""), 2000);
  };

  // Spustí akci (snímek/kopírování) bez kradení focusu editovanému prvku.
  const handleAction = async (e, fn, msg) => {
    e.preventDefault();
    try {
      await fn();
      showFeedback(msg);
    } catch (err) {
      showFeedback("Chyba ✗");
    }
  };

  return (
    <div
      ref={dragRef}
      className={styles.panel}
      style={{ left: pos.x, top: pos.y }}
      // Modal se nesmí objevit na snímku obrazovky (html2canvas).
      data-html2canvas-ignore="true"
      // Klik kdekoli v panelu nesmí zrušit editaci pod ním.
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className={styles.header} onMouseDown={startDrag}>
        <span className={styles.title}>
          <FiEdit3 aria-hidden /> Speciální znaky
        </span>
        <div className={styles.headerActions}>
          <button
            className={styles.iconBtn}
            title="Kopírovat upravený text prvku do schránky"
            onMouseDown={(e) => handleAction(e, onCopyText, "Text zkopírován ✓")}
          >
            <FaRegCopy aria-hidden />
          </button>
          <button
            className={styles.iconBtn}
            title="Snímek obrazovky do schránky"
            onMouseDown={(e) =>
              handleAction(e, onSnapshotClipboard, "Snímek ve schránce ✓")
            }
          >
            <FiCamera aria-hidden />
          </button>
          <button
            className={styles.iconBtn}
            title="Snímek obrazovky uložit jako PNG"
            onMouseDown={(e) =>
              handleAction(e, onSnapshotFile, "Snímek uložen ✓")
            }
          >
            <FiDownload aria-hidden />
          </button>
          <span className={styles.divider} />
          <button
            className={styles.iconBtn}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCollapsed((c) => !c);
            }}
            title={collapsed ? "Rozbalit" : "Sbalit"}
          >
            {collapsed ? <FiMaximize2 aria-hidden /> : <FiMinus aria-hidden />}
          </button>
          <button
            className={styles.iconBtn}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            title="Zavřít a ukončit editaci"
          >
            <FiX aria-hidden />
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className={styles.body}>
          {GROUPS.map((group) => (
            <div key={group.title} className={styles.group}>
              <div className={styles.groupTitle}>{group.title}</div>
              <div className={styles.chips}>
                {group.items.map((item, i) => (
                  <button
                    key={item.label + i}
                    className={styles.chip}
                    title={item.title}
                    onMouseDown={(e) => handleInsert(e, item.char)}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div className={styles.group}>
            <div className={styles.groupTitle}>Ikony</div>
            <div className={styles.chips}>
              {ICON_IDS.map((id) => (
                <button
                  key={id}
                  className={`${styles.chip} ${styles.iconChip}`}
                  title={id}
                  onMouseDown={(e) => handleInsertIcon(e, id)}
                >
                  <svg className={styles.iconChipSvg} aria-hidden>
                    <use xlinkHref={`${icons}#${id}`} href={`${icons}#${id}`} />
                  </svg>
                </button>
              ))}
            </div>
          </div>
          <div className={styles.hint}>
            {feedback || "Enter = potvrdit · Esc = zrušit · Alt+klik = jiný prvek"}
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCharsModal;
